import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Check, Coins, Infinity, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
{
  id: 'pack_15',
  name: '15 heures',
  credits: 15,
  price: 380,
  pricePerHour: 25.33,
  features: ['15 séances de 1h', 'Valable sans limite'],
  popular: false
},
{
  id: 'pack_30',
  name: '30 heures',
  credits: 30,
  price: 680,
  pricePerHour: 22.67,
  savings: 80,
  features: ['30 séances de 1h', 'Économisez 80€', 'Valable sans limite'],
  popular: false
},
{
  id: 'pack_45',
  name: '45 heures',
  credits: 45,
  price: 990,
  pricePerHour: 22,
  savings: 150,
  features: ['45 séances de 1h', 'Économisez 150€', 'Valable sans limite'],
  popular: true
},
{
  id: 'pack_60',
  name: '60 heures',
  credits: 60,
  price: 1320,
  pricePerHour: 22,
  savings: 200,
  features: ['60 séances de 1h', 'Économisez 200€', 'Valable sans limite'],
  popular: false
}

];

export default function Credits() {
  const { user, token, dbUser, refreshToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handlePurchase = async (packageId) => {
    if (!user || !token) {
      toast.error('Veuillez vous connecter');
      navigate('/login');
      return;
    }

    setLoading(packageId);
    try {
      const response = await axios.post(
        `${API}/credits/purchase`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'achat');
    } finally {
      setLoading(null);
    }
  };

  const hasUnlimited = dbUser?.unlimited_until && new Date(dbUser.unlimited_until) > new Date();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="font-heading uppercase tracking-widest text-[#D4FF00] text-sm mb-2">
              Crédits d'entraînement
            </p>
            <h1 className="font-heading font-black text-4xl sm:text-5xl mb-4">
              Achetez vos crédits
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              1 crédit = 1 heure d'entraînement = 1 séance.
            </p>
          </div>

          {/* Current Credits */}
          {dbUser && (
            <Card className="mb-8 border-2 border-[#D4FF00]/30" data-testid="current-credits-card">
              <CardContent className="py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D4FF00] rounded-lg flex items-center justify-center">
                      {hasUnlimited ? (
                        <Infinity className="w-6 h-6 text-black" />
                      ) : (
                        <Coins className="w-6 h-6 text-black" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Votre solde actuel</p>
                      {hasUnlimited ? (
                        <p className="font-heading font-bold text-2xl">Accès Illimité</p>
                      ) : (
                        <p className="font-heading font-bold text-2xl">{dbUser.credits?.toFixed(1) || '0'} heures</p>
                      )}
                    </div>
                  </div>
                  {hasUnlimited && (
                    <Badge className="bg-[#D4FF00] text-black">
                      Expire le {new Date(dbUser.unlimited_until).toLocaleDateString('fr-FR')}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Packages Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="packages-grid">
            {PACKAGES.map((pkg) => (
              <Card 
                key={pkg.id}
                className={`relative overflow-hidden ${
                  pkg.popular ? 'border-2 border-[#D4FF00] shadow-[0_0_15px_rgba(212,255,0,0.2)]' : ''
                }`}
                data-testid={`package-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 right-0 bg-[#D4FF00] text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    POPULAIRE
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-2xl">{pkg.name}</CardTitle>
                  <CardDescription>
                    {pkg.unlimited ? 'Accès illimité' : `${pkg.credits} crédit${pkg.credits > 1 ? 's' : ''}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <span className="font-heading font-black text-4xl">{pkg.price}€</span>
                    {!pkg.unlimited && (
                      <span className="text-muted-foreground text-sm ml-2">
                        ({pkg.pricePerHour}€/h)
                      </span>
                    )}
                  </div>
                  
                  {pkg.savings && (
                    <Badge variant="secondary" className="mb-4">
                      Économisez {pkg.savings}€
                    </Badge>
                  )}

                  <ul className="space-y-2 mb-6">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#D4FF00]" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full font-bold uppercase tracking-wide ${
                      pkg.popular 
                        ? 'bg-[#D4FF00] text-black hover:bg-[#bfe600]' 
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    }`}
                    onClick={() => handlePurchase(pkg.id)}
                    disabled={loading === pkg.id}
                    data-testid={`buy-${pkg.id}`}
                  >
                    {loading === pkg.id ? 'Traitement...' : 'Acheter'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Info */}
          <div className="mt-12 text-center text-sm text-muted-foreground">
            <p>Paiement sécurisé par Stripe. Les crédits n'expirent jamais (sauf formule illimitée).</p>
            <p className="mt-1">Besoin d'aide ? Contactez-nous à support@prokick.com</p>
          </div>
        </div>
      </main>
    </div>
  );
}
